import React, { memo, Fragment } from 'react';
import PropTypes from 'prop-types';
import StarRatings from 'react-star-ratings';
import Cookie from 'js-cookie';
import Router from 'next/router';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { setUpProduct } from '../../redux/actions';
import { Link } from '../../routes';
import {
  Marker,
  slugify,
  getExperienceName,
  getAmenityName
} from '../../utils';

import {
  CardTag,
  CardImageContainer,
  CardType,
  CardDiscount,
  CardInfo,
  CardHotelInfo,
  CardDescription,
  CardHeading,
  CardWay,
  CardLocation,
  CardFooter,
  CardRatingWrapper,
  CardVotes,
  Availability,
  CardPrice,
  CrossedPrice,
  CardWithDiscountPrice,
  FeaturesList,
} from './styles';

const LOW_STOCK_THRESHOLD = 5;

const handleCardClick = (handleProductSetup, hotelId, productName) => {
  const { router } = Router;
  const { asPath } = router;

  handleProductSetup({ hotelId, productName });

  Cookie.set('referer', asPath, { path: '/' });
};

const HotelSearchCard = ({
  id,
  experience,
  productName,
  reviewsTotal,
  rating,
  hotelCity,
  hotelName,
  image,
  description,
  price,
  oldPrice,
  amenities,
  isComingSoon,
  isAvailable,
  spotsLeft,
  distance,
  IsKidAllow,
  hotelId,
  handleProductSetup,
  badgesEnabled,
  promo,
  status
}) => {
  const priceFormatted = price.toFixed(2);
  const oldPriceFormatted = oldPrice.toFixed(2);

  const renderPriceTag = () => {
    let content = null;

    if (price < oldPrice) {
      content = (
        <Fragment>
          <CrossedPrice>${oldPriceFormatted}</CrossedPrice>
          <CardWithDiscountPrice>
            <span>${priceFormatted}</span>/guest
          </CardWithDiscountPrice>
        </Fragment>
      );
    } else {
      content = (
        <CardPrice>
          <span>${priceFormatted}</span>/guest
        </CardPrice>
      );
    }

    return content;
  };

  const renderAvailability = () => {
    if (isComingSoon) {
      return <Availability isRight={price < oldPrice}>Coming soon</Availability>;
    }
    if (!isAvailable) {
      return <Availability $unavailable isRight={price < oldPrice}>Unavailable</Availability>;
    }
    if (spotsLeft > 0 && spotsLeft <= LOW_STOCK_THRESHOLD) {
      return (
        <Availability $scarcity isRight={price < oldPrice}>
          {`Only ${spotsLeft} left`}
        </Availability>
      );
    }
    return null;
  };

  const amenitiesArray = Boolean(IsKidAllow) ? [...amenities, 7] : amenities;

  let linkParams = {
      marketName: slugify(hotelCity),
      hotelName: slugify(hotelName),
      hid: hotelId,
      pid: id,
    };

  if (status === 'success' && promo) {
    linkParams = {...linkParams, promo};
  }

  return (
    <Link
      route="product"
      params={linkParams}
    >
      <a target="_blank">
        <CardTag onClick={() => handleCardClick(handleProductSetup, hotelId, productName)}>
          <CardImageContainer>
            <CardType type={getExperienceName(experience)}>
              {getExperienceName(experience)}
            </CardType>
            <img src={image && `${process.env.CDN_URL}${image}`} alt={hotelName} />
            {price < oldPrice && (
              <CardDiscount>
                {Math.round(100 - (priceFormatted / oldPrice) * 100)}% off
              </CardDiscount>
            )}
          </CardImageContainer>
          <CardInfo>
            <CardHotelInfo>
              <CardWay>{productName}</CardWay>
              <CardRatingWrapper aria-label={`Hotel rating is ${rating === 0 ? 5 : rating} stars`}>
                <CardVotes>{reviewsTotal || ''}</CardVotes>
                <StarRatings
                  rating={rating === 0 ? 5 : rating}
                  numberOfStars={5}
                  starRatedColor="#1eb6ff"
                  starEmptyColor="#b6e4f9"
                  starDimension="15px"
                  starSpacing="0px"
                />
              </CardRatingWrapper>
              <CardLocation>
                <Marker width={15} height={19} />
                {hotelCity} {distance ? `(~${distance.toFixed(2)} miles)` : ''}
              </CardLocation>
            </CardHotelInfo>
            <CardHeading>{hotelName}</CardHeading>
            <CardDescription>
              {`${description.replace(/<\/?[^>]+(>|$)/g, '').substr(0, 100)}...`}
            </CardDescription>
            <CardFooter>
              <FeaturesList>
                {amenitiesArray.map(amenity => (
                  <li key={getAmenityName(amenity).name} title={getAmenityName(amenity).name}>
                    {getAmenityName(amenity).icon}
                  </li>
                ))}
              </FeaturesList>
              {badgesEnabled && renderAvailability()}
              {renderPriceTag()}
            </CardFooter>
          </CardInfo>
        </CardTag>
      </a>
    </Link>
  );
};

HotelSearchCard.propTypes = {
  experience: PropTypes.number.isRequired,
  distance: PropTypes.number,
  hotelName: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  productName: PropTypes.string.isRequired,
  hotelCity: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  amenities: PropTypes.arrayOf(PropTypes.number),
  rating: PropTypes.number.isRequired,
  reviewsTotal: PropTypes.number.isRequired,
  price: PropTypes.number.isRequired,
  oldPrice: PropTypes.number,
  isComingSoon: PropTypes.bool,
  isAvailable: PropTypes.bool,
  spotsLeft: PropTypes.number,
  IsKidAllow: PropTypes.number,
  hotelId: PropTypes.number,
  handleProductSetup: PropTypes.func,
  id: PropTypes.number,
  badgesEnabled: PropTypes.bool,
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      handleProductSetup: setUpProduct,
    },
    dispatch,
  );

const mapStateToProps = state => ({
  status: state.promo.status,
  promo: state.promo.info.code,
});

export default memo(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(HotelSearchCard),
);
